import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { SubGrupoDTO, SubGrupoCreateDTO } from '../../../models/subgrupo.model';
import { SubGrupoService } from '../../../services/grupo/sub-grupo.service';
import { GrupoService } from '../../../services/grupo/grupo.service';
import { GrupoDTO } from '../../../models/grupo.model';
import { SafeStorageService } from '../../safe-storage.service';

@Component({
  selector: 'app-subgrupo-adicionar-dialog',
  templateUrl: './subgrupo-adicionar-dialog.component.html',
  styleUrls: ['./subgrupo-adicionar-dialog.component.scss'],
})
export class SubgrupoAdicionarDialogComponent implements OnChanges, OnInit, OnDestroy {

  // === INPUTS ===
  @Input() visible = false;
  /** ID de grupo pré-selecionado (opcional). Quando informado, pré-seleciona no multi-select. */
  @Input() grupoId: number | null | undefined = null;

  // === OUTPUTS ===
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() subgrupoCriado = new EventEmitter<SubGrupoDTO>();

  // === ESTADO INTERNO ===
  nome: string = '';
  salvando: boolean = false;
  carregandoGrupos: boolean = false;

  /** Opcoes disponiveis no multi-select de categorias */
  opcoesGrupos: { label: string; value: number }[] = [];

  /** IDs de grupos selecionados pelo usuario */
  gruposSelecionados: number[] = [];

  // === TRADUCOES ===
  texts: {
    titulo: string;
    subtitulo: string;
    labels_nome: string;
    labels_categorias: string;
    placeholders_nome_da_subcategoria: string;
    placeholders_selecione_categorias: string;
    botoes_fechar: string;
    botoes_guardar: string;
    mensagens_campos_obrigatorios: string;
    mensagens_subcategoria_cadastrada: string;
    mensagens_erro_cadastrar: string;
    mensagens_grupo_obrigatorio: string;
    mensagens_erro_carregar_grupos: string;
  } = {
    titulo: '',
    subtitulo: '',
    labels_nome: '',
    labels_categorias: '',
    placeholders_nome_da_subcategoria: '',
    placeholders_selecione_categorias: '',
    botoes_fechar: '',
    botoes_guardar: '',
    mensagens_campos_obrigatorios: '',
    mensagens_subcategoria_cadastrada: '',
    mensagens_erro_cadastrar: '',
    mensagens_grupo_obrigatorio: '',
    mensagens_erro_carregar_grupos: '',
  };

  private langSub?: Subscription;

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private subgrupoService: SubGrupoService,
    private grupoService: GrupoService,
    private safe: SafeStorageService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.resetar();
      this.carregarGrupos();
    }
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  // === METODOS PUBLICOS ===

  /** Submete o formulario de nova subcategoria */
  submit(): void {
    if (!this.nome || !this.nome.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.texts.mensagens_campos_obrigatorios,
      });
      return;
    }

    if (!this.gruposSelecionados || this.gruposSelecionados.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.texts.mensagens_grupo_obrigatorio,
      });
      return;
    }

    this.salvando = true;

    const body: SubGrupoCreateDTO = {
      nome: this.nome.trim(),
      grupo_id: this.gruposSelecionados,
      empresaId: Number(this.safe.getItem('id')),
    };

    this.subgrupoService.post('/subgrupo', body).subscribe({
      next: (subgrupoCriado: SubGrupoDTO) => {
        this.salvando = false;
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.texts.mensagens_subcategoria_cadastrada,
        });
        this.subgrupoCriado.emit(subgrupoCriado);
        this.resetar();
        this.fechar();
      },
      error: () => {
        this.salvando = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.texts.mensagens_erro_cadastrar,
        });
      },
    });
  }

  /** Fecha o dialog */
  fechar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  // === METODOS PRIVADOS ===

  /** Carrega as categorias (grupos) disponiveis via API */
  private carregarGrupos(): void {
    this.carregandoGrupos = true;
    this.grupoService.getGrupos().subscribe({
      next: (response: any) => {
        this.carregandoGrupos = false;
        const lista: GrupoDTO[] = Array.isArray(response) ? response : (response?.content ?? []);
        this.opcoesGrupos = lista.map((g: GrupoDTO) => ({
          label: g.nome,
          value: g.id as number,
        }));

        // Pre-selecionar o grupoId recebido via @Input, se existir
        if (this.grupoId != null) {
          this.gruposSelecionados = [this.grupoId];
        }
      },
      error: () => {
        this.carregandoGrupos = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.texts.mensagens_erro_carregar_grupos,
        });
      },
    });
  }

  /** Limpa o estado do formulario */
  private resetar(): void {
    this.nome = '';
    this.salvando = false;
    this.gruposSelecionados = [];
    this.opcoesGrupos = [];
  }

  /** Carrega traducoes do namespace select-subcategoria.dialog */
  private loadTranslations(): void {
    const prefix = 'select-subcategoria.dialog';
    this.texts = {
      titulo: this.translate.instant(`${prefix}.titulo`),
      subtitulo: this.translate.instant(`${prefix}.subtitulo`),
      labels_nome: this.translate.instant(`${prefix}.labels.nome`),
      labels_categorias: this.translate.instant(`${prefix}.labels.categorias`),
      placeholders_nome_da_subcategoria: this.translate.instant(`${prefix}.placeholders.nome_da_subcategoria`),
      placeholders_selecione_categorias: this.translate.instant(`${prefix}.placeholders.selecione_categorias`),
      botoes_fechar: this.translate.instant(`${prefix}.botoes.fechar`),
      botoes_guardar: this.translate.instant(`${prefix}.botoes.guardar`),
      mensagens_campos_obrigatorios: this.translate.instant(`${prefix}.mensagens.campos_obrigatorios`),
      mensagens_subcategoria_cadastrada: this.translate.instant(`${prefix}.mensagens.subcategoria_cadastrada`),
      mensagens_erro_cadastrar: this.translate.instant(`${prefix}.mensagens.erro_cadastrar`),
      mensagens_grupo_obrigatorio: this.translate.instant(`${prefix}.mensagens.grupo_obrigatorio`),
      mensagens_erro_carregar_grupos: this.translate.instant(`${prefix}.mensagens.erro_carregar_grupos`),
    };
  }
}
